import { LINE_HEIGHT } from '../constants.js'

export type SearchSpanStyle = {
  bold?: boolean
  italic?: boolean
  code?: boolean
}

export type SearchSpan = SearchSpanStyle & {
  text: string
  width: number
}

export type SearchLine = {
  spans: SearchSpan[]
  yPx: number
  separatorBefore?: string
}

export type SearchLayout = {
  lines: SearchLine[]
}

export type SearchRange = {
  start: number
  end: number
}

export type SearchHit = {
  blockId: string
  range: SearchRange
}

export type SearchableBlock = {
  id: string
  editedId: string
  createdId: string
  layout: SearchLayout
}

export type HitRect = {
  x: number
  y: number
  width: number
  height: number
}

export type SearchMark = {
  blockId: string
  range: SearchRange
  active: boolean
}

export type SpanMeasurer = (text: string, style: SearchSpanStyle) => number

// Layout distinguishes forced word wraps from whitespace boundaries. A space
// is the fallback for callers supplying separate lines without that hint.
export const LINE_JOINER = ' '

export function buildHaystack(layout: SearchLayout): string {
  return layout.lines
    .map((line, index) => (index ? line.separatorBefore ?? LINE_JOINER : '') + line.spans.map(span => span.text).join(''))
    .join('')
}

export function findRanges(haystack: string, query: string): SearchRange[] {
  if (!query.length) return []
  // Match the original string: lowercasing can change its UTF-16 length and
  // move every subsequent highlight. Escaping keeps this a literal substring.
  const pattern = new RegExp(query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'giu')
  const ranges: SearchRange[] = []
  let match: RegExpExecArray | null
  while ((match = pattern.exec(haystack)) !== null) {
    ranges.push({ start: match.index, end: match.index + match[0].length })
  }
  return ranges
}

export function collectHits(blocks: SearchableBlock[], query: string): SearchHit[] {
  if (!query.length) return []
  const ordered = [...blocks].sort((a, b) => {
    if (a.editedId !== b.editedId) return a.editedId > b.editedId ? -1 : 1
    if (a.createdId !== b.createdId) return a.createdId > b.createdId ? -1 : 1
    return 0
  })
  const hits: SearchHit[] = []
  ordered.forEach(block => {
    findRanges(buildHaystack(block.layout), query).forEach(range => {
      hits.push({ blockId: block.id, range })
    })
  })
  return hits
}

export function stepHitIndex(current: number, delta: number, total: number): number {
  if (total <= 0) return -1
  if (current < 0) return delta < 0 ? total - 1 : 0
  return ((current + delta) % total + total) % total
}

export function rangeToRects(layout: SearchLayout, range: SearchRange, measure: SpanMeasurer): HitRect[] {
  const rects: HitRect[] = []
  let offset = 0
  layout.lines.forEach((line, lineIndex) => {
    if (lineIndex > 0) offset += (line.separatorBefore ?? LINE_JOINER).length
    const lineStart = offset
    const lineLength = line.spans.reduce((length, span) => length + span.text.length, 0)
    const lineEnd = lineStart + lineLength
    offset = lineEnd
    const overlapStart = Math.max(range.start, lineStart)
    const overlapEnd = Math.min(range.end, lineEnd)
    if (overlapEnd <= overlapStart) return
    const x = measurePrefix(line, overlapStart - lineStart, measure)
    const xEnd = measurePrefix(line, overlapEnd - lineStart, measure)
    if (xEnd <= x) return
    rects.push({ x, y: line.yPx, width: xEnd - x, height: LINE_HEIGHT })
  })
  return rects
}

function measurePrefix(line: SearchLine, chars: number, measure: SpanMeasurer): number {
  let width = 0
  let remaining = chars
  for (const span of line.spans) {
    if (remaining <= 0) break
    if (remaining >= span.text.length) {
      width += span.width
      remaining -= span.text.length
      continue
    }
    width += measure(span.text.slice(0, remaining), span)
    remaining = 0
  }
  return width
}
